import ContentPage from '@/components/feature/ContentPage';

export default function SafetyPage() {
  return (
    <ContentPage
      title="CSSBuy Safety Center: Research More Safely"
      metaTitle="CSSBuy Safety: Avoid Scams, Fake Links and Stay Secure | CSSBuyBR"
      metaDescription="CSSBuy safety guide. Learn to identify fake websites, suspicious links, phishing and payment scams. Protect your account and personal information."
      subtitle="Links can become outdated, prices can change, and fake pages may try to imitate legitimate services. Learn to protect yourself."
      showToc
      sections={[
        { id: 'fake', title: 'Fake Websites' },
        { id: 'links', title: 'Suspicious Links' },
        { id: 'phishing', title: 'Phishing' },
        { id: 'payment', title: 'Payment Scams' },
        { id: 'account', title: 'Account Security' },
        { id: 'sellers', title: 'Suspicious Sellers' },
        { id: 'checklist', title: 'Safety Checklist' },
      ]}
      content={
        <div className="space-y-6">
          <section id="fake">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">How to Identify Fake CSSBuy Websites</h2>
            <p>Scammers sometimes create websites that look similar to legitimate platforms. These fake sites may try to steal your login information, payment details, or personal data.</p>
            <div className="grid sm:grid-cols-2 gap-3 my-4">
              {[
                'Check the URL carefully — misspellings are a red flag',
                'Look for HTTPS and valid security certificates',
                'Verify the domain through official community channels',
                'Be cautious of sites with poor design or broken features',
                'Never enter credentials on unfamiliar domains',
                'Cross-check with official social media accounts',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-background-100 text-sm text-foreground-700">
                  <i className="ri-shield-check-line text-accent-500 mt-0.5"></i>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          <section id="links">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Spotting Suspicious Links</h2>
            <p>Spreadsheet links and product links can become outdated or be replaced with malicious alternatives. Before clicking any link:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Hover over links to preview the destination URL</li>
              <li>Avoid shortened links without preview services</li>
              <li>Check if the link matches the expected domain</li>
              <li>Be wary of links shared in unofficial forums or DMs</li>
              <li>Report broken or suspicious links to community moderators</li>
            </ul>
          </section>

          <section id="phishing">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Avoiding Phishing Attempts</h2>
            <p>Phishing attacks try to trick you into revealing sensitive information through fake emails, messages, or websites. Common signs include:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Urgent language demanding immediate action</li>
              <li>Requests for passwords or payment information</li>
              <li>Emails from addresses that do not match official domains</li>
              <li>Generic greetings instead of your actual name</li>
              <li>Unexpected attachments or download requests</li>
            </ul>
            <p className="mt-2">When in doubt, contact the platform through official channels rather than responding to unsolicited messages.</p>
          </section>

          <section id="payment">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Payment Safety</h2>
            <p>Protect your financial information by following these guidelines:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Only use official payment methods on the verified website</li>
              <li>Never share credit card details through email or chat</li>
              <li>Use payment methods with buyer protection when possible</li>
              <li>Monitor your bank statements for unauthorized charges</li>
              <li>Keep records of all transactions and confirmations</li>
            </ul>
          </section>

          <section id="account">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Account Security Best Practices</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Use a strong, unique password for your account',
                'Enable two-factor authentication if available',
                'Do not reuse passwords across multiple platforms',
                'Log out after each session, especially on shared devices',
                'Keep your email account secure — it is the recovery method',
                'Monitor login notifications for unauthorized access',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-accent-50 text-sm text-foreground-700">
                  <i className="ri-lock-line text-accent-500 mt-0.5"></i>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          <section id="sellers">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Evaluating Sellers</h2>
            <p>Before purchasing from a seller through CSSBuy, research their reputation:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Check community reviews and feedback</li>
              <li>Look for seller ratings and transaction history</li>
              <li>Avoid sellers with numerous unresolved complaints</li>
              <li>Be cautious of sellers with unrealistic prices</li>
              <li>Verify that product photos match descriptions</li>
            </ul>
          </section>

          <section id="checklist">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Quick Safety Checklist</h2>
            <div className="bg-background-100 rounded-xl p-5">
              <ol className="list-decimal list-inside space-y-2 text-sm text-foreground-700">
                <li>Verify the website URL before logging in</li>
                <li>Check links before clicking</li>
                <li>Never share passwords or payment details via email</li>
                <li>Use unique passwords for each platform</li>
                <li>Enable two-factor authentication</li>
                <li>Research sellers before purchasing</li>
                <li>Request and review QC photos</li>
                <li>Keep records of all transactions</li>
                <li>Report suspicious activity to the platform</li>
                <li>Stay updated on known scams in the community</li>
              </ol>
            </div>
          </section>

          <div className="p-4 rounded-xl bg-secondary-50 border border-secondary-200/70">
            <p className="text-sm text-secondary-700 font-medium">
              <i className="ri-alert-line mr-1"></i>
              Always verify the official address, prices, payment methods, terms and shipping information before completing any purchase.
            </p>
          </div>

          <div className="mt-6 p-4 rounded-xl bg-primary-50 border border-primary-200/70">
            <h3 className="text-sm font-semibold text-primary-800 mb-2">Continue Shopping Safely</h3>
            <p className="text-sm text-primary-700 mb-3">Visit the verified main site to browse products with confidence.</p>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors">
              Visit Main Site
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      }
    />
  );
}